/**
 * Batch-board view model (#2436). Pure functions only: the board UI consumes
 * `BoardDay` and never the `GET /api/dispatch/board` wire shape, which gets
 * mapped in a single adapter module once that endpoint's types are generated.
 *
 * Stage and gate names ARE the wire keys (#2435) so the board has one
 * vocabulary, not two. The job statuses in `STAGE_JOB_STATUSES` are a
 * different thing — the `fulfillment_jobs.status` enum — and keep their names.
 */
import type { components } from '@/types/api.generated';

export const BOARD_STAGES = [
  'awaiting_dispatch',
  'awaiting_assignment',
  'awaiting_departure',
  'en_route',
  'delivered',
  'exception_or_cancelled',
] as const;
export type BoardStage = (typeof BOARD_STAGES)[number];

export const BOARD_GATE_KINDS = [
  'packaging_cutoff',
  'dispatch_cutoff',
  'delivery_window_end',
] as const;
export type BoardGateKind = (typeof BOARD_GATE_KINDS)[number];

/** Per-row numbers behind the board's cell sub-text and next-gate lines (#2473). */
export interface BoardSignals {
  preflightAnomalies: number;
  unassignedTasks: number;
  deliveredLate: number;
}

export interface BoardDay {
  date: string;
  counts: Record<BoardStage, number>;
  /** Live jobs only; cancelled tombstones count in `exception_or_cancelled` but not here (#2481). */
  total: number;
  /**
   * `localTime` is the merchant wall clock (HH:mm) for display; `atIso` is the
   * same instant in UTC, for "minutes until" arithmetic.
   */
  nextGate: { kind: BoardGateKind; localTime: string; atIso: string } | null;
  /** From the wire `rest_day`; the client never derives it from a weekday. */
  restDay: boolean;
  signals: BoardSignals;
}

/**
 * Sum of the six columns: the row size the board shows. Never `total`: `total`
 * counts live rows only while `exception_or_cancelled` also counts cancelled
 * tombstones (#2481).
 */
export function rowSize(day: BoardDay): number {
  return Object.values(day.counts).reduce((sum, n) => sum + n, 0);
}

export type BoardLine =
  | { kind: 'day'; day: BoardDay; tone: 'past' | 'today' | 'future' }
  | { kind: 'rest'; date: string }
  | { kind: 'collapsed'; from: string; to: string };

export interface BoardTarget {
  date: string;
  stage: BoardStage;
}

type JobStatus = keyof components['schemas']['DispatchPreview']['statusCounts'];

/**
 * Job statuses the delivery-job list can filter on for each stage. An
 * approximation by design: the list cannot express "no driver yet" or "first
 * stop started", so its row count may differ from the board cell.
 */
export const STAGE_JOB_STATUSES = {
  awaiting_dispatch: ['planned'],
  awaiting_assignment: ['dispatched'],
  awaiting_departure: ['dispatched'],
  en_route: ['in_progress', 'dispatched'],
  delivered: ['completed'],
  exception_or_cancelled: ['exception', 'cancelled'],
} as const satisfies Record<BoardStage, readonly JobStatus[]>;

/** Calendar arithmetic on YYYY-MM-DD in UTC space — no DST risk. */
export function addDays(date: string, days: number): string {
  const next = new Date(`${date}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + days);
  return next.toISOString().slice(0, 10);
}

/** The HGS default board range: today through today + 9, i.e. 10 dates (phase 1 behaviour). */
export const DEFAULT_BOARD_RANGE_DAYS = 10;

/** Dates on the board: yesterday plus `rangeDays` days from today. */
export function boardDayCount(rangeDays: number): number {
  return rangeDays + 1;
}

/** Yesterday through today + `rangeDays` - 1: `boardDayCount(rangeDays)` dates. */
export function boardRange(today: string, rangeDays: number): { from: string; to: string } {
  const from = addDays(today, -1);
  return { from, to: addDays(from, boardDayCount(rangeDays) - 1) };
}

/**
 * The window the board shows and requests (epic #2469 C.3). A pure function of
 * `(today, selected, rangeDays)`: the board GET, the range label and the row
 * list all read it, none of them recompute it.
 *
 * - `selected` inside yesterday … today + `rangeDays` - 1: the window does not
 *   move; yesterday stays on top. `anchor: 'today'`.
 * - `selected` outside it: the window starts at `selected` and spans
 *   `rangeDays` dates. `anchor: 'selected'`.
 *
 * `labelFrom` / `labelTo` are the dates the range label shows. They leave out
 * yesterday, which only rides along as context.
 */
export interface BoardWindow {
  /** First date of the board GET and of the row list. */
  from: string;
  /** Number of dates from `from`, i.e. the board GET `days`. */
  days: number;
  anchor: 'today' | 'selected';
  labelFrom: string;
  labelTo: string;
}

export function boardWindow(today: string, selected: string, rangeDays: number): BoardWindow {
  const { from, to } = boardRange(today, rangeDays);
  if (selected >= from && selected <= to) {
    return {
      from,
      days: boardDayCount(rangeDays),
      anchor: 'today',
      labelFrom: today,
      labelTo: to,
    };
  }
  return {
    from: selected,
    days: rangeDays,
    anchor: 'selected',
    labelFrom: selected,
    labelTo: addDays(selected, rangeDays - 1),
  };
}

function windowDates(boardWin: BoardWindow): string[] {
  return Array.from({ length: boardWin.days }, (_, i) => addDays(boardWin.from, i));
}

/**
 * The tag class of a board date. A label class, not a time order: only
 * yesterday and today carry a tag, every other date reads as a plain row.
 */
export function dayTone(date: string, today: string): 'past' | 'today' | 'future' {
  if (date === today) return 'today';
  return date === addDays(today, -1) ? 'past' : 'future';
}

export function parseBoardStage(raw: string | null): BoardStage {
  return (BOARD_STAGES as readonly (string | null)[]).includes(raw)
    ? (raw as BoardStage)
    : 'awaiting_dispatch';
}

function emptyDay(date: string): BoardDay {
  return {
    date,
    counts: {
      awaiting_dispatch: 0,
      awaiting_assignment: 0,
      awaiting_departure: 0,
      en_route: 0,
      delivered: 0,
      exception_or_cancelled: 0,
    },
    total: 0,
    nextGate: null,
    restDay: false,
    signals: { preflightAnomalies: 0, unassignedTasks: 0, deliveredLate: 0 },
  };
}

/** Every date in the window in order, zero-filled where `days` has no row. */
function fillWindowDays(days: readonly BoardDay[], boardWin: BoardWindow): BoardDay[] {
  const byDate = new Map(days.map((d) => [d.date, d]));
  return windowDates(boardWin).map((date) => byDate.get(date) ?? emptyDay(date));
}

/**
 * Board lines for `boardWin`. Yesterday, today and the selected date are always
 * day lines — even when empty, even on a rest day — so the selected cell
 * always exists. Every other empty date folds into a run or a rest line.
 */
export function buildBoardLines(
  days: readonly BoardDay[],
  boardWin: BoardWindow,
  today: string,
  selected: string,
): BoardLine[] {
  const yesterday = addDays(today, -1);
  const lines: BoardLine[] = [];
  for (const day of fillWindowDays(days, boardWin)) {
    const pinned = day.date === yesterday || day.date === today || day.date === selected;
    if (!pinned && day.total === 0 && day.restDay) {
      // A rest day is its own line, never folded into a "no jobs" run.
      lines.push({ kind: 'rest', date: day.date });
      continue;
    }
    if (!pinned && day.total === 0) {
      const last = lines[lines.length - 1];
      if (last?.kind === 'collapsed') last.to = day.date;
      else lines.push({ kind: 'collapsed', from: day.date, to: day.date });
      continue;
    }
    lines.push({ kind: 'day', day, tone: dayTone(day.date, today) });
  }
  return lines;
}
